import { useEffect, useRef, useState } from 'react';

import type { SessionUrlResponse } from '@flintmint/core-shared/billing';
import { useToaster } from '@flintmint/web-shared/hooks/use-toaster';

import { useServices } from '@/providers/services-provider';
import type { ApiService } from '@/services/api-service';

type StripeRedirectOptions = {
  /** The error toast's title when the request fails. */
  errorTitle: string;
  /** Returns true when it handled the failure itself, so no error toast shows. */
  onError?: (error: unknown) => boolean;
};

/**
 * Leaves for the Stripe Checkout or portal URL a request answers with. `pending` holds the key of the redirect
 * under way and stays set while the page leaves; it clears on failure and when Back restores the page.
 */
export const useStripeRedirect = ({ errorTitle, onError }: StripeRedirectOptions) => {
  const { api } = useServices();
  const { showToastNotification } = useToaster();
  const [pending, setPending] = useState<string>();
  useResetOnPageRestore(() => setPending(undefined));

  const redirect = async (key: string, request: (api: ApiService) => Promise<SessionUrlResponse>) => {
    if (!api) return;
    setPending(key);
    try {
      const { url } = await request(api);
      // Stays pending: the page is leaving for Stripe.
      window.location.assign(url);
    } catch (error) {
      setPending(undefined);
      if (!onError?.(error)) showToastNotification({ type: 'error', title: errorTitle });
    }
  };

  return { redirect, pending };
};

/**
 * A button that stays busy while the page leaves for Stripe comes back busy when Back restores the page from
 * the back/forward cache with its state: `reset` runs on that restore.
 */
const useResetOnPageRestore = (reset: () => void) => {
  const resetRef = useRef(reset);
  useEffect(() => {
    resetRef.current = reset;
  });

  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) resetRef.current();
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);
};
